'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import { SectionTitle } from '@benzinga/core-ui';
import { StoryObject } from '@benzinga/basic-news-manager';
import { SimpleNewsQueryAndOptions } from '@benzinga/internal-news-manager';
import { TrackingManager } from '@benzinga/tracking-manager';
import { useWatchlistsLoading } from '@benzinga/watchlist-manager-hooks';
import { WatchlistCreateBox } from '@benzinga/widgets';
import { NewsList } from '../NewsList';
import { ContentFeedProps, ReachContentParams } from '../ContentFeed';
import { SessionContext } from '@benzinga/session-context';

export interface LatestNewsListProps {
  latestNewsQuery?: SimpleNewsQueryAndOptions;
  initialNodes: StoryObject[];
  reachContent?: ReachContentParams;
  showSponsoredContent?: boolean;
  showCommentsIcon?: boolean;
}

export interface LatestNewsTab extends ContentFeedProps {
  label: 'All News' | 'My Watchlist News';
  query?: SimpleNewsQueryAndOptions;
}

export const LatestNewsList: React.FC<LatestNewsListProps> = ({
  initialNodes,
  latestNewsQuery,
  showCommentsIcon,
  showSponsoredContent,
  ...props
}) => {
  const { watchlistSymbols } = useWatchlistsLoading();

  const tabs: LatestNewsTab[] = [
    {
      label: 'All News',
      query: latestNewsQuery,
    },
    {
      label: 'My Watchlist News',
      query: {
        tickers: watchlistSymbols?.join(',') ?? null,
      },
    },
  ];
  const session = React.useContext(SessionContext);
  const [currentTab, setCurrentTab] = React.useState<LatestNewsTab>(tabs[0]);
  const [nodes, setNodes] = React.useState<StoryObject[]>(initialNodes);

  const handleChangeTab = React.useCallback(
    (selectedTab: LatestNewsTab) => {
      if (currentTab.label !== selectedTab.label) {
        setNodes([]);
        setCurrentTab(selectedTab);
        session.getManager(TrackingManager).trackWidgetEvent('view', 'news_widget', {
          widget_preset: selectedTab.label,
          widget_sub_type: 'latest_news',
        });
      }
    },
    [session, currentTab],
  );

  const query = { excludeAutomated: true, ...currentTab.query };

  const feedHeaderElement = title => {
    return (
      <div className="news-block-header">
        <SectionTitle className="news-block-title">{title}</SectionTitle>
        <div className="news-block-tabs-list">
          {tabs.map((tab, index) => {
            return (
              <span key={tab.label}>
                <button
                  className={`${currentTab.label === tab.label ? 'active' : ''}`}
                  onClick={() => handleChangeTab(tab)}
                >
                  {tab.label}
                </button>
                {tabs.length - 1 !== index && <span className="divider">|</span>}
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Container className="latest-news-list">
      {(!watchlistSymbols || (Array.isArray(watchlistSymbols) && watchlistSymbols.length === 0)) &&
      currentTab.label === tabs[1].label ? (
        <>
          {feedHeaderElement('Latest')}
          <WatchlistCreateBox
            actionText="Get Alerts"
            buttonLocation="latest_news_list"
            subtitle="Create a Benzinga.com account for news on the stocks and cryptocurrencies you care about!"
            title="Get News and Alerts"
          />
        </>
      ) : (
        <NewsList
          {...props}
          contentId="Latest"
          feedHeaderElement={feedHeaderElement}
          key={currentTab.label}
          limit={4}
          loadMore={true}
          loadMoreDevice="all"
          nodes={nodes}
          query={query}
          showCommentsIcon={showCommentsIcon}
          showSponsoredContent={showSponsoredContent}
          title="Latest"
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  &.latest-news-list {
    .watchlist-create-box {
      margin-top: 20px;
    }

    .news-block-header {
      margin-bottom: 8px;
      h3 {
        font-size: 24px;
      }
    }

    .news-block-title {
      border-bottom: none;
    }
  }
`;
